import { useEffect, useState } from 'react'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core'
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, X } from 'lucide-react'
import { useBuilder } from './hooks/useBuilder'

/** Couleur de badge par catégorie de bloc */
const CATEGORY_COLORS = {
  Structure: 'bg-slate-500',
  Hero:      'bg-orange-500',
  Content:   'bg-blue-500',
  Statistics:'bg-emerald-500',
}

const getCategoryColor = (category) =>
  CATEGORY_COLORS[category] ?? 'bg-gray-400'

/* ── Item sortable ───────────────────────────────────────── */

const SortableItem = ({ bloc, registryEntry, isDragging }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: bloc.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
  }

  const label = registryEntry?.name ?? bloc.type
  const category = registryEntry?.category ?? ''

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2.5 px-3 py-2.5 bg-white rounded-lg border border-gray-200 shadow-sm group hover:border-teal-300 hover:shadow-md transition-all"
    >
      <button
        {...attributes}
        {...listeners}
        className="text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing flex-shrink-0 touch-none"
        aria-label="Drag to reorder"
      >
        <GripVertical size={16} />
      </button>

      <div className="flex-1 min-w-0">
        <span className="text-sm font-medium text-gray-800 truncate block">{label}</span>
      </div>

      {category && (
        <span className={`flex-shrink-0 text-[10px] font-semibold text-white px-1.5 py-0.5 rounded-full ${getCategoryColor(category)}`}>
          {category}
        </span>
      )}
    </div>
  )
}

/* ── Overlay (item en cours de drag) ─────────────────────── */

const DragItem = ({ bloc, registryEntry }) => {
  const label = registryEntry?.name ?? bloc.type
  const category = registryEntry?.category ?? ''

  return (
    <div className="flex items-center gap-2.5 px-3 py-2.5 bg-white rounded-lg border-2 border-teal-400 shadow-xl rotate-1 scale-105">
      <GripVertical size={16} className="text-teal-400 flex-shrink-0" />
      <span className="text-sm font-medium text-gray-800 truncate flex-1">{label}</span>
      {category && (
        <span className={`flex-shrink-0 text-[10px] font-semibold text-white px-1.5 py-0.5 rounded-full ${getCategoryColor(category)}`}>
          {category}
        </span>
      )}
    </div>
  )
}

/* ── Panneau principal ───────────────────────────────────── */

export const BuilderReorderPanel = ({ onClose }) => {
  const { page, registry, reorderBlocks } = useBuilder()
  const [items, setItems] = useState(page.blocs.map(b => b.id))
  const [activeId, setActiveId] = useState(null)

  // Sync si les blocs changent de l'extérieur pendant que le panneau est ouvert
  useEffect(() => {
    setItems(page.blocs.map(b => b.id))
  }, [page.blocs])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  )

  const blocById = Object.fromEntries(page.blocs.map(b => [b.id, b]))
  const regByType = Object.fromEntries(registry.map(r => [r.type, r]))

  const handleDragStart = ({ active }) => setActiveId(active.id)

  const handleDragEnd = ({ active, over }) => {
    setActiveId(null)
    if (!over || active.id === over.id) return
    const oldIndex = items.indexOf(active.id)
    const newIndex = items.indexOf(over.id)
    const next = arrayMove(items, oldIndex, newIndex)
    setItems(next)
    reorderBlocks(next)
  }

  const activeBloc = activeId ? blocById[activeId] : null

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-gray-900/20 backdrop-blur-[1px]"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panneau */}
      <aside className="fixed right-0 top-0 bottom-0 z-50 w-72 bg-gray-50 border-l border-gray-200 shadow-2xl flex flex-col animate-slide-in-right">

        {/* En-tête */}
        <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200">
          <div>
            <h2 className="text-sm font-semibold text-gray-800">Reorder blocks</h2>
            <p className="text-xs text-gray-400 mt-0.5">{items.length} block{items.length !== 1 ? 's' : ''} — drag to reorganize</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Liste */}
        <div className="flex-1 overflow-y-auto p-3">
          {items.length === 0 ? (
            <p className="text-center text-sm text-gray-400 mt-8">No blocks on this page.</p>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={items} strategy={verticalListSortingStrategy}>
                <div className="flex flex-col gap-2">
                  {items.map(id => {
                    const bloc = blocById[id]
                    if (!bloc) return null
                    return (
                      <SortableItem
                        key={id}
                        bloc={bloc}
                        registryEntry={regByType[bloc.type]}
                        isDragging={activeId === id}
                      />
                    )
                  })}
                </div>
              </SortableContext>

              <DragOverlay dropAnimation={{ duration: 180, easing: 'ease' }}>
                {activeBloc && (
                  <DragItem
                    bloc={activeBloc}
                    registryEntry={regByType[activeBloc.type]}
                  />
                )}
              </DragOverlay>
            </DndContext>
          )}
        </div>

        {/* Pied */}
        <div className="px-4 py-3 bg-white border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full py-2 rounded-lg text-sm font-medium bg-teal-700 text-white hover:bg-teal-800 transition-colors"
          >
            Done
          </button>
        </div>
      </aside>
    </>
  )
}
